// react
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Card, CardContent, Stack } from '@mui/material';

// api
import {
  useGetSinglePaMeterPortfolioMetersAnalysisTypeAccountIdMeterIdGetQuery,
  useSetMeterUtilityProviderMeterUtilityProviderAnalysisTypeAccountIdMeterIdPutMutation,
} from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisMeters';
import { useGetConfigurableUtilityProvidersConfigurableUtilityProvidersUtilityTypeGetQuery } from '@/api/generated/energyAnalysis/endpoints/utilityProviders';
import type { UtilityProvider } from '@/api/generated/energyAnalysis/endpoints/utilityProviders';

// shared components
// shared-components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import { RHFAutocomplete } from '@/components/ui/form';

// components
import type { AnalysisType } from '@/features/energyxplorer/types';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import {
  toEditMeterUtilityProviderFormValues,
  toUtilityProviderId,
} from './editMeterUtilityProviderForm.mappers';
import { createEditMeterUtilityProviderFormSchema } from './editMeterUtilityProviderForm.schema';

import { zodResolver } from '@hookform/resolvers/zod';

interface EditMeterUtilityProviderFormProps {
  accountId: number;
  meterId: number;
  analysisType: AnalysisType;
  onSuccess?: () => void;
  onCancel?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

export default function EditMeterUtilityProviderForm({
  accountId,
  meterId,
  analysisType,
  onSuccess,
  onCancel,
  onDirtyChange,
}: EditMeterUtilityProviderFormProps) {
  const { t } = useTranslation(['forms', 'validation']);
  const dispatch = useDispatch();

  const { data: meterData } =
    useGetSinglePaMeterPortfolioMetersAnalysisTypeAccountIdMeterIdGetQuery({
      accountId,
      meterId,
      analysisType,
    });

  const currentProviderId =
    'UtilityProviderId' in (meterData ?? {})
      ? (meterData as { UtilityProviderId?: string | null }).UtilityProviderId
      : null;

  const { data: providersData, isLoading: isLoadingProviders } =
    useGetConfigurableUtilityProvidersConfigurableUtilityProvidersUtilityTypeGetQuery(
      { utilityType: analysisType },
    );

  const [setUtilityProvider, { isLoading: isSaving }] =
    useSetMeterUtilityProviderMeterUtilityProviderAnalysisTypeAccountIdMeterIdPutMutation();

  const schema = createEditMeterUtilityProviderFormSchema(t);
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toEditMeterUtilityProviderFormValues(null),
    mode: 'onTouched',
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const onSubmit = handleSubmit(async (values) => {
    try {
      await setUtilityProvider({
        accountId,
        meterId,
        analysisType,
        utilityProviderId: toUtilityProviderId(values),
      }).unwrap();

      dispatch(
        showSnackbar({
          message: t('common.updateSuccess', { ns: 'forms' }),
          severity: 'success',
        }),
      );

      onSuccess?.();
    } catch {
      dispatch(
        showSnackbar({
          message: t('common.updateError', { ns: 'forms' }),
          severity: 'error',
        }),
      );
    }
  });

  const providers = (providersData?.providers ?? []) as UtilityProvider[];
  useEffect(() => {
    const list = providersData?.providers as UtilityProvider[] | undefined;
    if (!currentProviderId || !list?.length) return;
    const found = list.find((p) => p.provider_id === currentProviderId);
    if (found) reset({ provider: found });
  }, [currentProviderId, providersData?.providers, reset]);

  return (
    <form onSubmit={onSubmit} noValidate>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <RHFAutocomplete<
              { provider: UtilityProvider | null },
              UtilityProvider
            >
              name="provider"
              control={control}
              options={providers}
              loading={isLoadingProviders}
              getOptionLabel={(option) => option.provider_name}
              isOptionEqualToValue={(option, value) =>
                option.provider_id === value.provider_id
              }
              label={t('editMeterUtilityProvider.provider.label', {
                ns: 'forms',
              })}
              placeholder={t('editMeterUtilityProvider.provider.placeholder', {
                ns: 'forms',
              })}
              helperText={t('editMeterUtilityProvider.provider.helperText', {
                ns: 'forms',
              })}
              required
            />
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          {onCancel && (
            <CancelButton onClick={onCancel} disabled={isSaving}>
              {t('common.cancel', { ns: 'forms' })}
            </CancelButton>
          )}
          <SubmitButton
            loading={isSaving}
            disabled={!isValid || !isDirty || isSaving}
          >
            {isSaving
              ? t('editMeterUtilityProvider.submitting', { ns: 'forms' })
              : t('editMeterUtilityProvider.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
